import React from 'react';

/**
 * Product & Service Offerings — the operator-facing catalog of what the
 * platform sells. Frames the two engagement models (self-serve vs managed /
 * done-for-you), the modular products, and the professional-services add-ons.
 * A reference/positioning surface; not yet wired to billing.
 */

interface Engagement {
  key: string;
  icon: string;
  name: string;
  tagline: string;
  who: string;
  included: string[];
  pricing: string;
  accent: string;
}

const ENGAGEMENTS: Engagement[] = [
  {
    key: 'self-serve',
    icon: '🧑\u200d💻',
    name: 'Self-Serve Platform',
    tagline: 'You run your event on the platform.',
    who: 'Teams with their own staff who want full control and the lowest cost.',
    included: [
      'Branded tenant workspace (your subdomain)',
      'Seating, ticketing & check-in modules',
      'Your own Super User / Editor / User accounts',
      'Guest QR cards & self check-in',
      'Email support',
    ],
    pricing: 'Subscription or per-event license',
    accent: '#4f46e5',
  },
  {
    key: 'managed',
    icon: '🤝',
    name: 'Managed / Done-For-You',
    tagline: 'We execute the event for you.',
    who: 'Organizations that want to hand off setup and day-of operations.',
    included: [
      'Everything in Self-Serve, plus:',
      'We configure events, import orders & design seating',
      'On-site check-in staffing & coordination',
      'Dedicated event producer + support plan',
      'Post-event reporting & handover',
    ],
    pricing: 'Service engagement (per event / retainer)',
    accent: '#059669',
  },
];

interface Product {
  icon: string;
  name: string;
  blurb: string;
  selfServe: boolean;
  managed: boolean;
}

const PRODUCTS: Product[] = [
  { icon: '🪑', name: 'Seating & Floorplan', blurb: 'Table/seat assignment, floorplan grid, family grouping, wayfinding.', selfServe: true, managed: true },
  { icon: '🎟️', name: 'Ticketing & Orders', blurb: 'Guest records, ticket tiers, order import, reconciliation.', selfServe: true, managed: true },
  { icon: '✅', name: 'Check-In & Entry', blurb: 'QR/token check-in, packet pickup, reverse check-in, gate counts.', selfServe: true, managed: true },
  { icon: '🧭', name: 'Guest Experience', blurb: 'Public guest cards, concierge/FAQ, wayfinding, help desk.', selfServe: true, managed: true },
  { icon: '🎨', name: 'Branding & Theming', blurb: 'Tenant logo, colors, and custom domain on the hub.', selfServe: true, managed: true },
  { icon: '📊', name: 'Reporting & Insights', blurb: 'Attendance, seating utilization, and post-event summaries.', selfServe: true, managed: true },
];

interface Service {
  icon: string;
  name: string;
  blurb: string;
}

const SERVICES: Service[] = [
  { icon: '🚀', name: 'Onboarding & Setup', blurb: 'We stand up your workspace, events, venues, and seating map.' },
  { icon: '📥', name: 'Data Import', blurb: 'We ingest your order/guest files and reconcile ticket numbers.' },
  { icon: '🪄', name: 'Seating Design', blurb: 'We build your floorplan and run family/VIP seating assignments.' },
  { icon: '🧑\u200d✈️', name: 'On-Site Operations', blurb: 'Trained staff run check-in and entry on event day.' },
  { icon: '🛟', name: 'Support Plans', blurb: 'Tiered support with response-time SLAs and a named contact.' },
  { icon: '🎓', name: 'Training', blurb: 'We train your team on seating, ticketing, and check-in.' },
];

export function PlatformOfferingsPage() {
  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Product &amp; Service Offerings</h1>
          <p style={styles.sub}>
            Customers can run their event on the platform themselves — or we can execute it for them as a service.
          </p>
        </div>
      </div>

      {/* Two engagement models */}
      <div style={styles.engGrid}>
        {ENGAGEMENTS.map((e) => (
          <section key={e.key} style={{ ...styles.engCard, borderTopColor: e.accent }}>
            <div style={styles.engTop}>
              <span style={styles.engIcon}>{e.icon}</span>
              <div>
                <div style={styles.engName}>{e.name}</div>
                <div style={styles.engTagline}>{e.tagline}</div>
              </div>
            </div>
            <div style={styles.engWho}>{e.who}</div>
            <ul style={styles.engList}>
              {e.included.map((i) => (
                <li key={i} style={styles.engItem}>
                  <span style={{ ...styles.check, color: e.accent }}>✓</span> {i}
                </li>
              ))}
            </ul>
            <div style={{ ...styles.engPricing, color: e.accent }}>{e.pricing}</div>
          </section>
        ))}
      </div>

      {/* Modular products */}
      <h2 style={styles.sectionTitle}>Modules (à la carte)</h2>
      <section style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Module</th>
              <th style={styles.thCenter}>Self-Serve</th>
              <th style={styles.thCenter}>Managed</th>
            </tr>
          </thead>
          <tbody>
            {PRODUCTS.map((p) => (
              <tr key={p.name}>
                <td style={styles.td}>
                  <div style={styles.pName}>
                    <span style={styles.pIcon}>{p.icon}</span> {p.name}
                  </div>
                  <div style={styles.pBlurb}>{p.blurb}</div>
                </td>
                <td style={styles.tdCenter}>{p.selfServe ? '✓' : '—'}</td>
                <td style={styles.tdCenter}>{p.managed ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Professional services */}
      <h2 style={styles.sectionTitle}>Professional services</h2>
      <div style={styles.svcGrid}>
        {SERVICES.map((s) => (
          <div key={s.name} style={styles.svcCard}>
            <span style={styles.svcIcon}>{s.icon}</span>
            <div style={styles.svcName}>{s.name}</div>
            <div style={styles.svcBlurb}>{s.blurb}</div>
          </div>
        ))}
      </div>

      <div style={styles.footNote}>
        Positioning reference for the operator. Pricing and packaging are illustrative — billing and
        self-serve signup are planned (see Capabilities).
      </div>
    </div>
  );
}

const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { marginBottom: 18 },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0', maxWidth: 620 },
  engGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 },
  engCard: { background: '#fff', border: `1px solid ${BORDER}`, borderTop: '4px solid', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  engTop: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 },
  engIcon: { fontSize: '2rem' },
  engName: { fontWeight: 800, fontSize: '1.15rem', color: INK },
  engTagline: { color: MUTED, fontSize: '0.88rem', marginTop: 2 },
  engWho: { fontSize: '0.84rem', color: '#334155', background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 12 },
  engList: { listStyle: 'none', padding: 0, margin: 0 },
  engItem: { fontSize: '0.86rem', color: '#334155', padding: '5px 0', display: 'flex', gap: 8, alignItems: 'flex-start' },
  check: { fontWeight: 800 },
  engPricing: { marginTop: 14, fontWeight: 700, fontSize: '0.85rem' },
  sectionTitle: { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, margin: '26px 0 12px' },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 6, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  thCenter: { textAlign: 'center', padding: '10px 12px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', width: 110 },
  td: { padding: '12px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  tdCenter: { padding: '12px', borderBottom: '1px solid #f0f1f5', textAlign: 'center', color: '#166534', fontWeight: 800 },
  pName: { fontWeight: 700, color: INK, display: 'flex', alignItems: 'center', gap: 8 },
  pIcon: { fontSize: '1.1rem' },
  pBlurb: { color: MUTED, fontSize: '0.8rem', marginTop: 2 },
  svcGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  svcCard: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  svcIcon: { fontSize: '1.5rem' },
  svcName: { fontWeight: 700, color: INK, fontSize: '0.95rem', marginTop: 6 },
  svcBlurb: { color: MUTED, fontSize: '0.82rem', marginTop: 4, lineHeight: 1.45 },
  footNote: { marginTop: 24, fontSize: '0.78rem', color: MUTED, fontStyle: 'italic' },
};
